import { logger } from "@/utils/logger";
import { supabase } from '@/integrations/supabase/client';

export interface FormEmail {
  Department: string;
  ADF_email: string;
  HTML_email: string;
}

export interface FormEmails {
  form_emails: FormEmail[];
}

export interface EmailExtractionResult {
  success: boolean;
  form_emails?: FormEmail[];
  dealer_id?: number;
  error?: string;
}

/**
 * Encrypts email configuration for a dealer (generates encrypted token)
 */
export async function encryptEmailConfig(
  emailConfig: object,
  dealerId: number,
  domain: string
): Promise<{ success: boolean; encryptedToken?: string; error?: string }> {
  // Function removed - no longer storing form emails
  return { success: false, error: 'Email encryption no longer supported' };
}

/**
 * Extracts form emails from page using encrypted token
 */
export async function getFormEmailsFromPage(formSelector?: string): Promise<EmailExtractionResult> {
  logger.log('🔑 getFormEmailsFromPage CALLED with formSelector:', formSelector);
  
  // Check if we're in a development/Lovable environment
  const isDevelopment = window.location.hostname.includes('lovableproject.com') || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname.startsWith('127.0.0.1');
  
  if (isDevelopment) {
    logger.log('🚧 Development environment detected - skipping encrypted email token extraction');
    return { 
      success: false, 
      error: 'Email token extraction not available in development environment'
    };
  }
  
  const encryptedToken = extractEncryptedEmailToken(formSelector);
  
  if (!encryptedToken) {
    logger.log('❌ NO_ENCRYPTED_TOKEN_FOUND');
    return { success: false, error: 'No encrypted email token found on page' };
  }

  logger.log('✅ ENCRYPTED_TOKEN_FOUND, processing...');
  const currentDomain = window.location.hostname;
  logger.log('🌐 CURRENT_DOMAIN:', currentDomain);
  return await processEncryptedEmailToken(encryptedToken, currentDomain);
}

/**
 * Processes encrypted email token from a website
 */
export async function processEncryptedEmailToken(
  encryptedToken: string, 
  requestDomain: string
): Promise<EmailExtractionResult> {
  // Function removed - no longer processing form emails
  return { success: false, error: 'Form email processing no longer supported' };
}

/**
 * Extracts encrypted email token from a form or page
 */
export function extractEncryptedEmailToken(formSelector?: string): string | null {
  logger.log('🔍 EXTRACTING_EMAIL_TOKEN - formSelector:', formSelector);
  const form = formSelector ? document.querySelector(formSelector) : document;
  if (!form) {
    logger.log('❌ FORM_NOT_FOUND');
    return null;
  }

  const hiddenInput = form.querySelector('input[name="form_emails"]') as HTMLInputElement;
  logger.log('🔍 HIDDEN_INPUT_FOUND:', !!hiddenInput);
  logger.log('🔍 HIDDEN_INPUT_VALUE_LENGTH:', hiddenInput?.value?.length || 0);
  
  const token = hiddenInput?.value || null;
  logger.log('🔍 EXTRACTED_TOKEN:', token ? 'TOKEN_FOUND' : 'NO_TOKEN');
  return token;
}

/**
 * Helper function to get specific email by form type and email type
 */
export function getEmailByType(
  formEmails: FormEmail[], 
  formType: string, 
  emailType: 'HTML_email' | 'ADF_email'
): string | null {
  const email = formEmails?.find(email => email.Department === formType);
  return email?.[emailType] || null;
}